import { createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaFacets } from '@algolia/autocomplete-preset-algolia';
import * as ElasticAppSearch from '@elastic/app-search-javascript';
import algoliasearch from 'algoliasearch/lite';
import { useMemo, useState } from 'react';

import { getSearchSuggestions as getPagefindSearchSuggestions } from '~/components/SearchResults/SearchService/providers/pagefind';

import { useCurrentLang } from '../useCurrentLang';
import { AutocompleteItemType, AutocompleteStateType } from './models';

export const useAutocomplete = () => {
  const [autocompleteState, setAutocompleteState] =
    useState<AutocompleteStateType>();
  const { currentLang } = useCurrentLang();

  const { getInputProps } = useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }: { state: AutocompleteStateType }) {
          setAutocompleteState(state);
        },
        getSources() {
          return [
            {
              sourceId: 'suggestions',
              async getItems({ query }) {
                if (query.length < 3) {
                  return [];
                }

                // TODO: move logic to Search Service (where other Elastic related code is located) when algolia is removed
                // https://dev.azure.com/RB-Group/IT_and_D_Project/_workitems/edit/423478
                if (process.env.GATSBY_ELASTICSEARCH_ENGINE) {
                  const elasticsearchClient = ElasticAppSearch.createClient({
                    searchKey: process.env.GATSBY_ELASTICSEARCH_SEARCH_KEY,
                    endpointBase: process.env.GATSBY_ELASTICSEARCH_URL,
                    engineName: process.env.GATSBY_ELASTICSEARCH_ENGINE,
                  });

                  const autocompleteResults =
                    await elasticsearchClient.querySuggestion(query, {
                      types: {
                        documents: {
                          fields: ['title', 'description'],
                        },
                      },
                      size: 3,
                    });

                  return autocompleteResults.results.documents.map(
                    ({ suggestion }) => ({ label: suggestion })
                  );
                }

                if (process.env.GATSBY_ALGOLIA_INDEX) {
                  const appId = process.env.GATSBY_ALGOLIA_APP_ID as string;
                  const searchId = process.env
                    .GATSBY_ALGOLIA_SEARCH_PUBLIC_KEY as string;
                  const indexName = process.env.GATSBY_ALGOLIA_INDEX as string;

                  const searchClient = algoliasearch(appId, searchId);

                  return getAlgoliaFacets<AutocompleteItemType>({
                    searchClient,
                    queries: [
                      {
                        indexName,
                        facet: 'seoKeywords',
                        params: {
                          facetQuery: query,
                          maxFacetHits: 3,
                        },
                      },
                    ],
                  });
                }

                return getPagefindSearchSuggestions(query, currentLang.locale);
              },
            },
          ];
        },
      }),
    []
  );

  return { autocompleteState, getInputProps };
};
